import { AbstractControl, ValidationErrors, ValidatorFn, Validators, FormArray } from '@angular/forms';

import { StringUtils } from '../../../utils/string.utils';
import { ObjectUtils } from '../../../utils/object.utils';
import { ValidatorUtils } from '../../../utils/validator.utils';
import { ControlReference, ControlReferenceHelper } from './ControlReference';


export class FormValidators {
  static readonly PHONE_REGEX: string = '^(\\+48)?[0-9]{9}$';
  static readonly POSTAL_ZONE_REGEX: string = '^[0-9]{2}-[0-9]{3}$';
  static readonly EMAIL_REGEX = '^[a-zA-Z0-9\\.\\_\\%\\+\\-]*[a-zA-Z0-9\\_\\%\\+\\-]@([a-zA-Z0-9\\-\\.])+\\.([a-zA-Z]){2,64}$';


  static required(): ValidatorFn {
    return Validators.compose([Validators.required, this.isNotEmpty()]);
  }

  static requiredTrue(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      return Validators.requiredTrue(control);
    };
  }

  static email(): ValidatorFn {
    return this.pattern(this.EMAIL_REGEX, 'email');
  }

  static isNotEmpty(): ValidatorFn {
    return (control: AbstractControl) => {
      return this.isEmptyInputValue(control.value) || StringUtils.isEmptyOrWhitespaces(control.value) ? { required: true } : null;
    };
  }

  // have to be declared before required validator if there is one
  static fraction(fraction: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.dirty && control.value) {
        const value = this.round(control.value, fraction);
        ValidatorUtils.setValueWithoutValidation(control, value);
      }
      return null;
    };
  }

  // have to be declared before required validator if there is one
  static min(min: number, exclusive: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isEmptyInputValue(control.value) || this.isEmptyInputValue(min)) {
        return null;
      }
      if (control.dirty) {
        const value = parseFloat(control.value);
        if (!isNaN(value) && (exclusive ? value <= min : value < min)) {
          ValidatorUtils.setValueWithoutValidation(control, exclusive ? Math.ceil(min) + 1 : min);
        }
      }
      return null;
    };
  }

  // have to be declared before required validator if there is one
  static max(max: number, exclusive: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isEmptyInputValue(control.value) || this.isEmptyInputValue(max)) {
        return null;
      }
      if (control.dirty) {
        const value = parseFloat(control.value);
        if (!isNaN(value) && (exclusive ? value >= max : value > max)) {
          ValidatorUtils.setValueWithoutValidation(control, exclusive ? Math.floor(max) - 1 : max);
        }
      }
      return null;
    };
  }

  static phoneNumber(): ValidatorFn {
    return this.pattern(this.PHONE_REGEX, 'phone');
  }

  static nipNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isNipValid(control.value)) {
        return { nip: true };
      }
      return null;
    };
  }

  static nipFormat(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isNipFormatValid(control.value)) {
        return { nipFormat: true };
      }
      return null;
    };
  }

  static nipControlNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isNipControlNumberValid(control.value)) {
        return { nipControlNumber: true };
      }
      return null;
    };
  }

  static nipWithCountry(country: ControlReference): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const countryCode = this.getCountryCode(country, control);
      return this.validateNipByCountry(control, countryCode);
    };
  }

  private static validateNipByCountry(control: AbstractControl, countryCode: string): ValidationErrors {
    if (!control || !control.value) {
      return null;
    }

    if (countryCode === 'PL') {
      return this.getNipValidationErrors(control.value);
    }
    return null;
  }

  static getNipValidationErrors(value: string): ValidationErrors {
    if (value && !ValidatorUtils.isNipFormatValid(value)) {
      return { nipFormat: true };
    }
    if (value && !ValidatorUtils.isNipValid(value)) {
      return { nip: true };
    }
    if (value && !ValidatorUtils.isNipControlNumberValid(value)) {
      return { nipControlNumber: true };
    }

    return null;
  }

  static regonFormat(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isRegonFormatValid(control.value)) {
        return { regonFormat: true };
      }
      return null;
    };
  }

  static regonControlNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isRegonControlNumberValid(control.value)) {
        return { regonControlNumber: true };
      }
      return null;
    };
  }

  private static getCountryCode(country: ControlReference, control: AbstractControl): string {
    const reference = ControlReferenceHelper.getControlReference(control, country);
    return reference ? reference.value : null;
  }

  static glnNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !ValidatorUtils.isGlnValid(control.value)) {
        return { gln: true };
      }
      return null;
    };
  }

  static postalZone(): ValidatorFn {
    return this.pattern(FormValidators.POSTAL_ZONE_REGEX, 'postalzone');
  }

  static postalZoneWithCountry(country?: ControlReference): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (this.isPostalZoneInvalid(control, country)) {
        return { postalzone: true };
      }
      return null;
    };
  }

  private static isPostalZoneInvalid(control: AbstractControl, country?: ControlReference): boolean {
    const countryCode = this.getCountryCode(country, control);

    return control && control.value && countryCode === 'PL'
      && !FormValidators.test(control.value, FormValidators.POSTAL_ZONE_REGEX);
  }

  static fromNumber(fieldNameTo: string) {
    return (control: AbstractControl): ValidationErrors => {
      if (control.parent == null) {
        return null;
      }

      const fieldTo = control.parent.get(fieldNameTo);

      if (!fieldTo.value) {
        return null;
      }

      if (control.value > fieldTo.value) {
        return { moreThan: fieldNameTo };
      }

      ValidatorUtils.executeWithoutValidation(control, () => fieldTo.updateValueAndValidity({ emitEvent: false }));

      return null;
    };
  }

  static toNumber(fieldNameFrom: string) {
    return (control: AbstractControl): ValidationErrors => {
      if (control.parent == null) {
        return null;
      }

      const fieldFrom = control.parent.get(fieldNameFrom);

      if (!fieldFrom.value) {
        return null;
      }

      if (control.value < fieldFrom.value) {
        return { lessThan: fieldNameFrom };
      }

      ValidatorUtils.executeWithoutValidation(control, () => fieldFrom.updateValueAndValidity({ emitEvent: false }));

      return null;
    };
  }

  static minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value.length < min) {
        return {
          minArrayLength: {
            actual: control.value.length,
            expected: min
          }
        };
      }
      return null;
    };
  }

  static maxArrayLength(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value.length > max) {
        return {
          maxArrayLength: {
            actual: control.value.length,
            expected: max
          }
        };
      }
      return null;
    };
  }

  // have to be declared as last validator
  static refreshField(fieldName: string) {
    return (control: AbstractControl): ValidationErrors => {
      if (control.invalid) {
        return null;
      }

      if (control.parent == null) {
        return null;
      }

      const field = control.parent.get(fieldName);

      field.updateValueAndValidity({ emitEvent: false });
      field.markAsTouched();

      return null;
    };
  }

  static pattern(pattern: string, outputField: string = 'pattern'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value && !this.test(control.value, pattern)) {
        return { [outputField]: true };
      }
      return null;
    };
  }

  static patternNotNull(pattern: string, outputField: string = 'pattern'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (!this.test(control.value, pattern)) {
        return { [outputField]: true };
      }
      return null;
    };
  }

  static test(value: any, regex: string): boolean {
    try {
      const regExp: RegExp = new RegExp(regex);
      return regExp.test(value.toString());
    } catch (error) {
      return false;
    }
  }

  private static round(value: number, fraction: number): number {
    const multiplier = this.getMultiplier(fraction);

    if (multiplier === 1) {
      return value;
    }
    return Math.round(value * multiplier) / multiplier;
  }

  private static getMultiplier(digits: number): number {
    if (!digits) {
      return 1;
    }
    const base = 10;
    return Math.pow(base, digits);
  }

  private static isEmptyInputValue(value: any): boolean {
    return value == null || value.length === 0;
  }
}
